import React, { memo, useState, useEffect, useRef } from "react";
import "./Footer.css";
// Libraries
import { Link } from "react-router-dom";
import { PropTypes } from "prop-types";
import { m, AnimatePresence } from "framer-motion";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../hooks/useLanguage";
// Components
import MessageBox from "./MessageBox";
import { Input } from "./Form";
import { resetForm, subscribeNewsletterUser } from "../Functions/Utilities";
import EnvelopeIcon from "Assets/img/envelope_icon.svg";

// Import Lottie
import Lottie from "lottie-react";
// Import your animation data
import facebookAnimData from "Assets/img/fb_anim.json";
import instagramAnimData from "Assets/img/ig_anim.json";

import { initCookieConsent } from "../cookieConsent";

// Modify the JSON data to change color to white
const whiteColorFilter = (animationData) => {
  // Deep clone the animation data to avoid modifying the original
  const modifiedData = JSON.parse(JSON.stringify(animationData));

  // Function to recursively change color values to white
  const changeToWhite = (obj) => {
    if (obj && typeof obj === "object") {
      // Check if this is a color array [r,g,b,a] and change to white [1,1,1,1]
      if (obj.c && obj.c.k && Array.isArray(obj.c.k) && obj.c.k.length === 4) {
        obj.c.k = [1, 1, 1, 1]; // White in RGBA
      }

      // Look for "c" property with "k" array in fills
      if (obj.ty === "fl" && obj.c && obj.c.k) {
        if (Array.isArray(obj.c.k)) {
          obj.c.k = [1, 1, 1, 1]; // White in RGBA
        }
      }

      // Iterate through all properties to find nested objects
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && typeof obj[key] === "object") {
          changeToWhite(obj[key]);
        }
      }
    }
  };

  // Process layers to change colors
  if (modifiedData.layers) {
    modifiedData.layers.forEach((layer) => {
      if (layer.shapes) {
        layer.shapes.forEach((shape) => {
          changeToWhite(shape);
        });
      }
    });
  }

  return modifiedData;
};

const whiteInstagramAnim = whiteColorFilter(instagramAnimData);
const whiteFacebookAnim = whiteColorFilter(facebookAnimData);

// Custom ScrollToTop Link component
const ScrollLink = ({ to, className, children }) => {
  const handleClick = () => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  return (
    <Link to={to} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
};

const CookieConsentButton = ({ title, className }) => {
  return (
    <button type='button' data-cc='show-preferencesModal' className={className}>
      {title}
    </button>
  );
};

const ArrowRightLong = ({ className, ...props }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 512 512'
    className={className}
    {...props}
  >
    <path d='M502.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-128-128c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l370.7 0-73.4 73.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l128-128z' />
  </svg>
);

// Custom SectionScrollLink component that scrolls to a specific section
const SectionScrollLink = ({ to, className, children }) => {
  const handleClick = (e) => {
    e.preventDefault();
    const sectionId = to.split("#")[1];
    if (window.location.pathname !== "/") {
      // Store the section ID in sessionStorage before navigation
      sessionStorage.setItem("scrollToSection", sectionId);
      window.location.href = "/"; // Navigate to homepage without hash
      return;
    }
    // If we're already on the homepage, just scroll to the section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <a href={to} className={className} onClick={handleClick}>
      {children}
    </a>
  );
};

// Social Media Icon with Lottie animation that plays only on hover
const SocialMediaIcon = ({ animationData, url, ariaLabel, className }) => {
  const [isHovered, setIsHovered] = useState(false);
  const lottieRef = useRef(null);

  useEffect(() => {
    if (lottieRef.current) {
      if (isHovered) {
        // Play animation when hovered
        lottieRef.current.setDirection(1);
        lottieRef.current.goToAndPlay(0, true);
      } else {
        // Pause animation when not hovered
        lottieRef.current.pause();
        // Go to first frame to show static icon
        lottieRef.current.goToAndStop(0, true);
      }
    }
  }, [isHovered]);

  return (
    <a
      href={url}
      target='_blank'
      rel='noopener noreferrer'
      aria-label={ariaLabel}
      className={`social-icon-link ${className || ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <div className='lottie-container max-w-[2em]'>
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          autoplay={false}
          loop={false}
          className='social-icon-anim'
        />
      </div>
    </a>
  );
};

// Data generator with translations
const generateFooterData = (t, getLocalizedPath) => [
  {
    title: t('footer.solutionsFor'),
    links: [
      { title: t('footer.content'), link: getLocalizedPath("/") },
      { title: t('footer.brand'), link: getLocalizedPath("/") },
      { title: t('footer.analytics'), link: getLocalizedPath("/") },
      {
        title: t('footer.aiAutomation'),
        link: "https://ai.inaq.sk",
        newWindow: true,
      },
    ],
  },
  {
    title: t('footer.aboutUs'),
    links: [
      { title: t('footer.blog'), link: getLocalizedPath("/blog"), scrollTop: true },
      { title: t('footer.contact'), link: getLocalizedPath("/#contact"), scrollToSection: true },
      {
        title: t('footer.career'),
        link: getLocalizedPath("/#careers"),
        scrollToSection: true,
        badge: "1",
      },
      { title: t('footer.termsOfUse'), link: getLocalizedPath("/tos"), newWindow: true },
      { title: t('footer.privacySettings'), cookieConsent: true },
    ],
  },
];

const Footer = (props) => {
  const { t } = useTranslation('common');
  const { getLocalizedPath } = useLanguage();
  const [formStatus, setFormStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Generate translated footer data
  const FooterData = generateFooterData(t, getLocalizedPath);

  return (
    <footer className='footer-inaq w-full max-w-[100vw] overflow-x-hidden xs:pt-20 xs:pl-4 xxs:pl-0'>
      <div className='container'>
        <div className='footer-content md:!grid md:!justify-center md:pl-20 '>
          <div className='about-section lg:hidden block'>
            <img
              className='logo logo-footer blog-logo-footer'
              alt='INAQ logo web'
              src={props.logo}
              width='32'
              height='57'
              loading='lazy'
            />
            <p className='about-text pr-10 cursor-default'>
              {t('footer.companyDescription')}
            </p>
            {/* Add social media icons with hover-triggered animations */}
            <div className='social-icons-wrapper mt-3 flex'>
              <SocialMediaIcon
                animationData={whiteFacebookAnim}
                url='https://facebook.com/inaq.sk'
                ariaLabel='Facebook'
                className='mr-3'
              />
              <SocialMediaIcon
                animationData={whiteInstagramAnim}
                url='https://instagram.com/inaq.sk'
                ariaLabel='Instagram'
              />
            </div>
          </div>
          <div className='newsletter-section lg:pl-8 md:pl-0 md:mb-16'>
            <div className='newsletter-header'>
              <h2 className='newsletter-title cursor-default text-white md:!text-left md:!px-0'>
                {t('footer.newsletterTitle')}
              </h2>
              <p className='newsletter-text cursor-default' dangerouslySetInnerHTML={{ __html: t('footer.newsletterSubtitle') }} />
            </div>
            <div className='newsletter-form'>
              <Formik
                initialValues={{ email: "" }}
                validationSchema={Yup.object().shape({
                  email: Yup.string()
                    .email(t('forms.invalidEmail'))
                    .required(t('forms.requiredField')),
                })}
                onSubmit={async (values, actions) => {
                  actions.setSubmitting(true);
                  const response = await subscribeNewsletterUser(values);

                  if (response.status === "success") {
                    resetForm(actions);
                    setFormStatus("success");
                    setTimeout(() => setFormStatus(null), 5000);
                  } else {
                    actions.setSubmitting(false);
                    setFormStatus("error");
                    setErrorMessage(
                      response.message || t('footer.newsletterError')
                    );
                    setTimeout(() => setFormStatus(null), 5000);
                  }
                }}
              >
                {({ isSubmitting, status }) => (
                  <div className='form-container relative'>
                    <Form className='form' noValidate>
                      <Input
                        showErrorMsg={false}
                        type='email'
                        name='email'
                        className='email-input'
                        placeholder={t('footer.emailPlaceholder')}
                      />
                      <button
                        aria-label='subscribe'
                        type='submit'
                        className={`submit-button${
                          isSubmitting ? " loading" : ""
                        }`}
                      >
                        <i className='m-0 text-lg'>
                          <img
                            src={EnvelopeIcon}
                            alt=''
                            width='18'
                            height='18'
                            className='envelope-icon'
                          />
                        </i>
                      </button>
                    </Form>
                    <AnimatePresence>
                      {(status || formStatus === "success") && (
                        <m.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className='absolute top-[115%] left-0 w-full'
                        >
                          <MessageBox
                            className='py-[8px] rounded-[2px] !text-[13px] font-rubik'
                            theme='message-box01'
                            variant='success'
                            message={t('footer.newsletterSuccess')}
                          />
                        </m.div>
                      )}
                      {formStatus === "error" && (
                        <m.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className='absolute top-[115%] left-0 w-full'
                        >
                          <MessageBox
                            className='py-[8px] rounded-[2px] !text-[13px] font-rubik'
                            theme='message-box01'
                            variant='error'
                            message={errorMessage}
                          />
                        </m.div>
                      )}
                    </AnimatePresence>
                    <div className='form-divider' />
                    <p className='form-terms cursor-default'>
                      <span>
                        {t('footer.newsletterTerms')}{" "}
                      </span>
                      <a
                        href={getLocalizedPath('/tos')}
                        className='terms-link'
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{
                          color: "inherit",
                          transition: "color 0.3s ease",
                        }}
                        onMouseEnter={(e) => (e.target.style.color = "#fff")}
                        onMouseLeave={(e) => (e.target.style.color = "inherit")}
                      >
                        {t('footer.privacyPolicy')}
                      </a>
                      <span>.</span>
                    </p>
                  </div>
                )}
              </Formik>
            </div>
          </div>
          <div className='footer-navigation xs:gap-2 xxs:!gap-1'>
            {FooterData.map((column, index) => (
              <div key={index} className='nav-column'>
                <h3 className='column-title cursor-default'>{column.title}</h3>
                <div className='column-links'>
                  {column.links.map((link, linkIndex) => (
                    <div key={linkIndex} className='link-item'>
                      {link.cookieConsent ? (
                        <CookieConsentButton
                          title={link.title}
                          className='nav-link'
                        />
                      ) : link.newWindow ? (
                        <a
                          href={link.link}
                          className='nav-link'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {link.title}
                        </a>
                      ) : link.scrollTop ? (
                        <ScrollLink to={link.link} className='nav-link'>
                          {link.title}
                        </ScrollLink>
                      ) : link.scrollToSection ? (
                        <SectionScrollLink to={link.link} className='nav-link'>
                          {link.title}
                        </SectionScrollLink>
                      ) : (
                        <Link to={link.link} className='nav-link'>
                          {link.title}
                        </Link>
                      )}
                      {link.badge && (
                        <div className='badge cursor-default'>
                          <span className='badge-text'>{link.badge}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className='footer-bottom w-full overflow-hidden'>
        <div className='max-w-[1280px] w-full mx-auto px-10'>
          <div className='divider w-full' />
          <div className='copyright-section'>
            <p className='copyright cursor-default'>
              © {new Date().getFullYear()} {t('footer.copyright')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

Footer.defaultProps = {
  logo: "/assets/img/webp/inaq_logo_2x_web_white.webp",
};

Footer.propTypes = {
  className: PropTypes.string,
  logo: PropTypes.string,
};

export default memo(Footer);
